import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import ReferenceList from '../../components/health/ReferenceList';
import principlesData from '../../data/health/principles';

const SectionBlock = ({ section, children }) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <motion.section
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isVisible ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="health-content"
    >
      <h2>{section.title}</h2>
      {section.intro && <p>{section.intro}</p>}
      {children}
    </motion.section>
  );
};

const HealthPrinciples = () => {
  const { nutrition, movement, sleep, antiAging, dosAndDonts, references } = principlesData;

  return (
    <>
      <Helmet>
        <title>Long-Term Health Principles | Health</title>
        <meta name="description" content="Evidence-backed principles for nutrition, movement, sleep, and anti-aging that hold true regardless of age or trend." />
        <meta property="og:title" content="Long-Term Health Principles" />
        <meta property="og:description" content="The evergreen doctrine — nutrition, movement, sleep, and anti-aging fundamentals backed by research." />
      </Helmet>

      <div className="health-page">
        <h1 className="health-page-title">{principlesData.title}</h1>
        <p className="text-gray-300" style={{ maxWidth: 640, marginBottom: '2.5rem' }}>
          {principlesData.subtitle}
        </p>

        {/* Nutrition */}
        <SectionBlock section={nutrition}>
          {nutrition.points.map((point, i) => (
            <div key={i} style={{ marginBottom: '1.25rem' }}>
              <h3>{point.heading}</h3>
              <p dangerouslySetInnerHTML={{ __html: formatCitations(point.text) }} />
            </div>
          ))}
        </SectionBlock>

        <hr className="health-divider" />

        {/* Movement */}
        <SectionBlock section={movement}>
          {movement.points.map((point, i) => (
            <div key={i} style={{ marginBottom: '1.25rem' }}>
              <h3>{point.heading}</h3>
              <p dangerouslySetInnerHTML={{ __html: formatCitations(point.text) }} />
            </div>
          ))}
        </SectionBlock>

        <hr className="health-divider" />

        {/* Sleep */}
        <SectionBlock section={sleep}>
          {sleep.points.map((point, i) => (
            <div key={i} style={{ marginBottom: '1.25rem' }}>
              <h3>{point.heading}</h3>
              <p dangerouslySetInnerHTML={{ __html: formatCitations(point.text) }} />
            </div>
          ))}
        </SectionBlock>

        <hr className="health-divider" />

        {/* Anti-Aging */}
        <div className="health-content">
          <h2>{antiAging.title}</h2>

          <h3>{antiAging.evidenceBacked.title}</h3>
          <ul>
            {antiAging.evidenceBacked.items.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: formatCitations(item) }} />
            ))}
          </ul>

          <h3>{antiAging.overhyped.title}</h3>
          {antiAging.overhyped.items.map((item, i) => (
            <div key={i} className="health-callout warning" style={{ marginBottom: '0.75rem' }}>
              <div className="health-callout-title">{item.name}</div>
              <div className="health-callout-text" dangerouslySetInnerHTML={{ __html: formatCitations(item.caveat) }} />
            </div>
          ))}
        </div>

        <hr className="health-divider" />

        {/* Do's and Don'ts Table */}
        <div className="health-content">
          <h2>{dosAndDonts.title}</h2>
          <div style={{ overflowX: 'auto' }}>
            <table className="health-table">
              <thead>
                <tr>
                  <th className="do-col">Do</th>
                  <th className="dont-col">Avoid / Limit</th>
                </tr>
              </thead>
              <tbody>
                {dosAndDonts.categories.map((cat) => (
                  <React.Fragment key={cat.name}>
                    <tr className="category-row">
                      <td colSpan={2}>{cat.name}</td>
                    </tr>
                    {cat.items.map((row, j) => (
                      <tr key={j}>
                        <td dangerouslySetInnerHTML={{ __html: formatCitations(row.do) }} />
                        <td dangerouslySetInnerHTML={{ __html: formatCitations(row.dont) }} />
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* References */}
        <div className="health-content">
          <ReferenceList references={references} />
        </div>
      </div>
    </>
  );
};

// Converts [1], [2] etc. in text to clickable anchor links
function formatCitations(text) {
  return text.replace(
    /\[(\d+)\]/g,
    '<a href="#ref-$1" class="health-cite">[$1]</a>'
  );
}

export default HealthPrinciples;
